import { useEffect, useState } from "react";
import client, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ImageIcon, Save } from "lucide-react";

const FORMAT_OPTIONS = [
  { value: "jpeg", label: "JPEG" },
  { value: "png", label: "PNG" },
  { value: "webp", label: "WebP" },
  { value: "gif", label: "GIF" },
];

/**
 * Limits enforced on branch/employee photo uploads (server.py's
 * upload_branch_photo/upload_employee_photo) and on the employee-photo
 * import pipeline (employee_photos.py). Max size applies to both; allowed
 * formats + thumbnail size are employee-photo-only — branch photos are
 * always re-encoded to JPEG client-side before upload, so those two settings
 * wouldn't mean anything there.
 */
export function PhotoUploadSettingsCard() {
  const [maxSizeMb, setMaxSizeMb] = useState("5");
  const [formats, setFormats] = useState(["jpeg", "png", "webp", "gif"]);
  const [thumbnailPx, setThumbnailPx] = useState("200");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get("/settings");
        const map = Object.fromEntries(data.map((s) => [s.key, s.value]));
        if (map.photo_max_size_mb != null) setMaxSizeMb(String(map.photo_max_size_mb));
        if (Array.isArray(map.photo_allowed_formats)) setFormats(map.photo_allowed_formats);
        if (map.photo_thumbnail_size_px != null) setThumbnailPx(String(map.photo_thumbnail_size_px));
      } catch {
        // No permission or nothing saved yet — the card just shows its defaults.
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleFormat = (value) => {
    setFormats((prev) => (prev.includes(value) ? prev.filter((f) => f !== value) : [...prev, value]));
  };

  const save = async () => {
    const sizeNum = Number(maxSizeMb);
    const thumbNum = Number(thumbnailPx);
    if (!Number.isFinite(sizeNum) || sizeNum <= 0) {
      toast.error("Max file size must be a positive number");
      return;
    }
    if (!Number.isFinite(thumbNum) || thumbNum <= 0) {
      toast.error("Thumbnail size must be a positive number");
      return;
    }
    setSaving(true);
    try {
      await Promise.all([
        client.put("/settings/photo_max_size_mb", { value: sizeNum }),
        client.put("/settings/photo_allowed_formats", { value: formats }),
        client.put("/settings/photo_thumbnail_size_px", { value: thumbNum }),
      ]);
      toast.success("Photo upload settings saved");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const disabled = loading || saving;

  return (
    <Card className="mb-6 p-6" data-testid="settings-photo-uploads-card">
      <div className="mb-1 flex items-center gap-2">
        <ImageIcon className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-lg font-semibold">Photo Uploads</h2>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">
        Limits enforced on branch and employee photo uploads. Allowed formats and thumbnail size apply
        to employee photos only — branch photos are always saved as JPEG.
      </p>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <Label className="data-label mb-1 block">Max file size (MB)</Label>
          <Input
            data-testid="settings-photo-max-size"
            type="number"
            min="1"
            step="1"
            value={maxSizeMb}
            onChange={(e) => setMaxSizeMb(e.target.value)}
            disabled={disabled}
            className="font-mono text-xs"
          />
          <p className="mt-1 text-[11px] text-muted-foreground">Applies to branch photos too.</p>
        </div>
        <div>
          <Label className="data-label mb-1 block">Employee photo thumbnail size (px, square)</Label>
          <Input
            data-testid="settings-photo-thumbnail-size"
            type="number"
            min="16"
            step="1"
            value={thumbnailPx}
            onChange={(e) => setThumbnailPx(e.target.value)}
            disabled={disabled}
            className="font-mono text-xs"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="data-label mb-2 block">Allowed employee photo formats</Label>
          <div className="flex flex-wrap gap-4">
            {FORMAT_OPTIONS.map((opt) => (
              <label key={opt.value} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  data-testid={`settings-photo-format-${opt.value}`}
                  checked={formats.includes(opt.value)}
                  disabled={disabled}
                  onChange={() => toggleFormat(opt.value)}
                  className="h-4 w-4 rounded border-border"
                />
                {opt.label}
              </label>
            ))}
          </div>
          {formats.length === 0 && (
            <p className="mt-2 text-[11px] text-destructive">
              No formats allowed — employee photo upload/download/import will be blocked entirely.
            </p>
          )}
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button
          data-testid="settings-photo-uploads-save"
          onClick={save}
          disabled={disabled}
          className="gap-1.5"
        >
          <Save className="h-3.5 w-3.5" />
          Save
        </Button>
      </div>
    </Card>
  );
}
